import colors from '../../theme/colors';
import PropTypes from 'prop-types';
import { HelpCircle, AlertCircle, Wifi } from 'lucide-react';
import ErrorDisplay from '../error/ErrorDisplay';

/**
 * ScopeErrorHandler - Specialized error handling for scope analysis
 *
 * This component provides context-aware error handling for scope analysis,
 * using the existing ErrorDisplay component with scope-specific configurations.
 */
const ScopeErrorHandler = ({
  status,
  error,
  hasQuotes = false,
  hasScopeFile = false,
  packageData = null,
  userMessage,
  suggestions,
  canRetry,
}) => {
  const getScopeErrorConfig = () => {
    // FIRST: Try to use backend error details (following tender analysis pattern)
    if (userMessage || suggestions) {
      return {
        code: 'SCOPE_ANALYSIS_ERROR',
        type: 'system_error',
        user_message: userMessage || 'Scope analysis failed',
        message:
          userMessage ||
          error?.message ||
          'An error occurred during scope analysis.',
        suggestions: suggestions || [
          'Try refreshing the page',
          'Contact support if the issue persists',
        ],
        severity: canRetry ? 'medium' : 'high',
        icon: <AlertCircle className="error-icon" />,
      };
    }

    // FALLBACK: Status-based handling only if no backend details
    switch (status) {
      case 'NOT_FOUND': {
        // Determine if this is "no scope file" vs "analysis missing"
        const actuallyHasScopeFile =
          hasScopeFile || packageData?.scope_of_works;

        if (!actuallyHasScopeFile) {
          return {
            code: 'NO_SCOPE_FILE',
            type: 'business_error',
            user_message: 'No scope analysis available',
            message:
              'No scope of works document was uploaded with this package.',
            suggestions: [
              'Scope analysis requires uploading a scope document during package creation',
              'You can still view the tender analysis results above',
              'Consider re-uploading the package with a scope document if analysis is needed',
            ],
            severity: 'low',
            icon: <HelpCircle className="info-icon" />,
          };
        } else {
          return {
            code: 'SCOPE_ANALYSIS_MISSING',
            type: 'validation_error',
            user_message: 'Scope analysis not available',
            message:
              'A scope document was uploaded but analysis results are not available.',
            suggestions: [
              'The analysis may have failed during processing',
              'The scope document format may not be supported',
              'Try refreshing to check if analysis has completed',
              'Contact support if you expected scope analysis results',
            ],
            severity: 'medium',
            icon: <AlertCircle className="warning-icon" />,
          };
        }
      }

      case 'server_error':
        return {
          code: 'SCOPE_SERVER_ERROR',
          type: 'system_error',
          user_message: 'Analysis server error',
          message:
            'A server error occurred while processing the scope analysis.',
          suggestions: [
            'This is typically a temporary issue',
            'Try refreshing the page or checking back later',
            'Contact support if the problem persists',
          ],
          severity: 'high',
          icon: <AlertCircle className="error-icon" />,
        };

      case 'network_error':
        return {
          code: 'SCOPE_NETWORK_ERROR',
          type: 'network_error',
          user_message: 'Connection error',
          message: 'Unable to fetch scope analysis due to a network error.',
          suggestions: [
            'Check your internet connection',
            'Try refreshing the page',
            'Contact support if connectivity issues persist',
          ],
          severity: 'medium',
          icon: <Wifi className="error-icon" />,
        };

      case 'bad_request':
        return {
          code: 'SCOPE_DATA_ERROR',
          type: 'system_error',
          user_message: 'Analysis data error',
          message: 'There was an issue with the scope analysis data format.',
          suggestions: [
            'This may indicate corrupted analysis data',
            'Try re-uploading the package if possible',
            'Contact support for assistance with data recovery',
          ],
          severity: 'high',
          icon: <AlertCircle className="error-icon" />,
        };

      default:
        return {
          code: 'SCOPE_UNKNOWN_ERROR',
          type: 'system_error',
          user_message: 'Unknown error',
          message: error?.message || 'An unexpected error occurred.',
          suggestions: [
            'Try refreshing the page',
            'Contact support if the issue persists',
          ],
          severity: 'medium',
          icon: <AlertCircle className="error-icon" />,
        };
    }
  };

  // Don't show anything if there's no error
  const skipStatuses = ['success', 'loading'];
  if (!status || skipStatuses.includes(status?.toLowerCase())) {
    return null;
  }

  const config = getScopeErrorConfig();

  // Create error object compatible with ErrorDisplay
  const errorForDisplay = {
    code: config.code,
    type: config.type,
    message: config.message,
    user_message: config.user_message,
    suggestions: config.suggestions,
    severity: config.severity,
    reference_id: error?.reference_id || `scope_${Date.now().toString(36)}`,
    details: {
      status,
      hasQuotes,
      hasScopeFile: hasScopeFile || packageData?.scope_of_works,
      originalError: error,
    },
  };

  return (
    <div className="scope-error-handler">
      <ErrorDisplay
        error={errorForDisplay}
        showSuggestions={true}
        showContactSupport={config.severity === 'high'}
        showErrorCode={true}
        showErrorDetails={false}
        variant="standard"
        sx={{
          mt: 0,
          mb: 2,
        }}
      />

      {/* Additional context and retry action */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '12px',
          padding: '12px',
          backgroundColor: colors.background.secondary,
          borderRadius: '6px',
          border: '1px solid #e9ecef',
        }}
      >
        {hasQuotes && (
          <div
            style={{
              fontSize: '13px',
              color: colors.grey.t500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <strong style={{ color: colors.green.success }}>Note:</strong>
            Tender analysis and comparison analysis results are available in
            other tabs. Only the scope analysis is affected by this issue.
          </div>
        )}
      </div>
    </div>
  );
};

ScopeErrorHandler.propTypes = {
  /**
   * Error status from scope analysis fetch
   */
  status: PropTypes.oneOf([
    // 'not_found',
    'NOT_FOUND',
    'server_error',
    'network_error',
    'bad_request',
    'unknown_error',
    'success',
    'loading',
  ]).isRequired,

  /**
   * Error object with details
   */
  error: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
    user_message: PropTypes.string,
    suggestions: PropTypes.arrayOf(PropTypes.string),
    canRetry: PropTypes.bool,
    reference_id: PropTypes.string,
  }),

  /**
   * Whether the package has quote documents
   */
  hasQuotes: PropTypes.bool,

  /**
   * Whether the package has a scope file uploaded
   */
  hasScopeFile: PropTypes.bool,

  /**
   * Package data object for additional context
   */
  packageData: PropTypes.shape({
    scope_of_works: PropTypes.object,
    name: PropTypes.string,
  }),

  /**
   * User-friendly error message from backend
   */
  userMessage: PropTypes.string,

  /**
   * Error suggestions from backend
   */
  suggestions: PropTypes.arrayOf(PropTypes.string),

  /**
   * Whether the analysis can be retried
   */
  canRetry: PropTypes.bool,
};

export default ScopeErrorHandler;
